import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription';
import { APP_ROUTES } from '../../config/routes';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { Crown, Building2, Briefcase, Rocket, Sparkles, type LucideIcon } from 'lucide-react';
import { tierHue, tintBadgeClass } from '../../config/colorTokens';

interface SubscriptionBadgeProps {
  className?: string;
  showIcon?: boolean;
  /** Show only the icon with no text label (for compact/collapsed contexts) */
  iconOnly?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

// Icon per known tier. Tier *colors* come from the shared categorical palette
// (config/colorTokens.ts) so badges agree wherever a tier is shown (#336).
const tierIcons: Record<string, LucideIcon> = {
  starter: Rocket,
  professional: Briefcase,
  business: Building2,
  partner: Crown,
  enterprise: Building2,
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-3 py-1 text-sm',
  lg: 'px-4 py-1.5 text-base',
};

const iconSizes = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

/**
 * Badge component to display the current subscription tier
 */
export default function SubscriptionBadge({
  className = '',
  showIcon = true,
  iconOnly = false,
  size = 'md',
}: SubscriptionBadgeProps) {
  const { data: tier, isLoading } = useSubscription();
  const { hasPermission } = useUserPermissions();
  const navigate = useNavigate();
  const canManageBilling = hasPermission('organizations:billing') || hasPermission('system:admin');

  // Show loading state only while actively fetching
  if (isLoading) {
    if (iconOnly) {
      return (
        <div
          className={`inline-flex items-center justify-center rounded-full border bg-zinc-100 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 p-1.5 ${className}`}
        >
          <Sparkles
            className={`${iconSizes[size]} text-zinc-400 dark:text-zinc-500 animate-pulse`}
          />
        </div>
      );
    }
    return (
      <div
        className={`inline-flex items-center rounded-full border bg-zinc-100 dark:bg-zinc-800 ${sizeClasses[size]} ${className}`}
      >
        <span className="text-zinc-500 dark:text-zinc-400">Loading...</span>
      </div>
    );
  }

  // Show N/A when no tier (e.g., user has no organization)
  if (!tier) {
    if (iconOnly) return null;
    return (
      <div
        className={`inline-flex items-center rounded-full border bg-zinc-100 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 ${sizeClasses[size]} ${className}`}
        title="No subscription (requires organization)"
      >
        <span className="text-zinc-500 dark:text-zinc-400">N/A</span>
      </div>
    );
  }

  // Resolve icon + categorical color for the tier (default to Sparkles/zinc).
  const Icon = tierIcons[tier.slug.toLowerCase()] ?? Sparkles;
  const tintClasses = tintBadgeClass(tierHue(tier.slug));

  const badgeClasses = iconOnly
    ? `inline-flex items-center justify-center rounded-full border ${tintClasses} p-1.5 font-medium ${className}`
    : `inline-flex items-center gap-1.5 rounded-full border ${tintClasses} ${sizeClasses[size]} font-medium ${className}`;

  const badgeContent = iconOnly ? (
    <Icon className={iconSizes[size]} />
  ) : (
    <>
      {showIcon && <Icon className={iconSizes[size]} />}
      <span>{tier.name}</span>
    </>
  );

  if (canManageBilling) {
    return (
      <button
        onClick={() => navigate(APP_ROUTES.SETTINGS_BILLING)}
        className={`${badgeClasses} cursor-pointer hover:opacity-80 transition-opacity`}
        title={`${tier.name} Tier · Manage subscription`}
      >
        {badgeContent}
      </button>
    );
  }

  return (
    <div className={badgeClasses} title={`${tier.name} Tier`}>
      {badgeContent}
    </div>
  );
}
